import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray } from 'redux-form/immutable';
import DeleteIcon from '@mui/icons-material/Delete';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import CheckIcon from '@mui/icons-material/Check';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Menu, MenuItem, FormControl, IconButton } from '@material-ui/core';

import FormCheckbox from 'retired/FormCheckbox';
import FormSwitch from 'retired/FormSwitch';
import makeSortableItem from 'retired/SortableItem';
import * as validators from 'utils/validators';
import FormTextField from 'retired/FormTextField';
import FormFieldLabel from 'retired/FormFieldLabel/index';
import FormDescriptionRteField from 'retired/FormDescriptionRteField';

import Options from './Options';
import LikertOptions from './LikertOptions';
import QuestionTypeSelector from './QuestionTypeSelector';

const required = validators.required();

const Question = ({ question, questionType, remove, sections, item, change, isSurvey }) => {
  const hasNextSections = item
    .get('typeOptions')
    ?.some((o) => o.get('nextSection') && o.get('nextSection') !== '-');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showNextSections, setShowNextSections] = useState(false);
  const effectiveShowNextSections = showNextSections || hasNextSections;
  const handleMenuOpen = () => {
    setMenuOpen(!menuOpen);
  };
  const handleMenuClose = () => {
    setMenuOpen(false);
  };
  const toggleNextSections = () => {
    if (effectiveShowNextSections) {
      if (change && item.get('typeOptions')) {
        // Reset nextSection values
        item.get('typeOptions').forEach((o, index) => {
          if (o.get('nextSection') && o.get('nextSection') !== '-') {
            change(`${question}.typeOptions[${index}].nextSection`, '-');
          }
        });
      }
      setShowNextSections(false);
    } else {
      setShowNextSections(true);
    }
    setMenuOpen(false);
  };
  const buttonRef = useRef();

  return (
    <div
      style={{
        border: '1px solid rgba(0, 0, 0, 0.2)',
        padding: 15,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <DragHandleIcon />
      </div>
      <div style={{ marginBottom: 30 }}>
        <Field
          name={`${question}.questionType`}
          component={QuestionTypeSelector}
          title="Question type"
          style={{ maxWidth: 220, marginBottom: 15 }}
        />
        <FormControl style={{ width: '100%' }}>
          <Field
            name={`${question}.title`}
            component={FormTextField}
            placeholder={questionType === 'Checkbox' ? 'Text' : 'Question'}
            validate={required}
            required
            multiline={questionType === 'Checkbox'}
          />
        </FormControl>
      </div>
      <div style={{ width: '100%', marginBottom: 30, marginRight: 48 }}>
        <Field
          name={`${question}.notes`}
          fullWidth
          multiline
          inputProps={{ autoComplete: 'off' }}
          component={FormDescriptionRteField}
          label="Additional notes"
          autoComplete="off"
          placeholder="Additional notes"
        />
      </div>
      {questionType === 'NPSScale' && (
        <div style={{ paddingLeft: 15 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              marginBottom: 10,
            }}
          >
            <FormFieldLabel style={{ width: 120 }}>0 (low) label:</FormFieldLabel>
            <FormControl>
              <Field
                name={`${question}.typeConfiguration.lowLabel`}
                component={FormTextField}
                placeholder="Optional label"
                title="0 (low) label"
              />
            </FormControl>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              marginBottom: 10,
            }}
          >
            <FormFieldLabel style={{ width: 120 }}>10 (high) label:</FormFieldLabel>
            <FormControl>
              <Field
                name={`${question}.typeConfiguration.highLabel`}
                component={FormTextField}
                placeholder="Optional label"
                title="10 (high) label"
              />
            </FormControl>
          </div>
        </div>
      )}
      {questionType === 'LikertScale' && (
        <FieldArray
          name={`${question}.typeOptions`}
          component={LikertOptions}
          props={{ questionType }}
        />
      )}
      {questionType !== 'LikertScale' && questionType !== 'NPSScale' && (
        <FieldArray
          name={`${question}.typeOptions`}
          component={Options}
          props={{
            question,
            questionType,
            sections,
            showNextSections: effectiveShowNextSections,
            allowMultiple: item.get('typeConfiguration')?.get('allowMultiple'),
            isSurvey,
          }}
        />
      )}
      {questionType === 'NonprofitSelector' && (
        <div style={{ marginTop: 5 }}>
          <Field
            name={`${question}.typeConfiguration.allowMultiple`}
            component={FormCheckbox}
            label="Allow multiple nonprofits"
          />
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <div
          style={{
            paddingRight: 10,
            marginRight: 20,
            borderRight: '1px solid rgba(0, 0, 0, 0.2)',
          }}
        >
          <IconButton title="Delete question" onClick={remove} tooltip="bottom-right">
            <DeleteIcon />
          </IconButton>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          Required?
          <Field name={`${question}.isRequired`} component={FormSwitch} title="Required" />
        </div>

        {(questionType === 'MultipleChoice' || questionType === 'SelectOneDropdown') && (
          <>
            <div ref={buttonRef}>
              <IconButton aria-haspopup="true" onClick={handleMenuOpen}>
                <MoreVertIcon />
              </IconButton>
            </div>
            <Menu
              anchorEl={buttonRef.current || document.body}
              keepMounted
              open={menuOpen}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={toggleNextSections}>
                {effectiveShowNextSections && <CheckIcon style={{ marginRight: 10 }} />} Go to
                section based on answer
              </MenuItem>
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};
Question.propTypes = {
  question: PropTypes.string,
  questionType: PropTypes.string,
  sections: PropTypes.object,
  item: PropTypes.object,
  change: PropTypes.func,
  remove: PropTypes.func,
  isSurvey: PropTypes.bool,
};
export default makeSortableItem({
  itemType: 'question',
  wrapperStyle: {
    position: 'relative',
    maxWidth: 1000,
    paddingTop: 6,
    paddingBottom: 2,
  },
})(Question);
